import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, clearSession, TOKEN_KEY, USER_KEY } from "@/lib/api";
import { L } from "@/i18n";

const AuthContext = createContext(null);

const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY));
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(readUser);
  const [checking, setChecking] = useState(() => !!localStorage.getItem(TOKEN_KEY));

  const logout = useCallback(() => {
    clearSession();
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    if (!token) return;
    api
      .get("/auth/me")
      .then(({ data }) => {
        const me = data.user ?? data;
        if (me.role !== "admin") return logout();
        localStorage.setItem(USER_KEY, JSON.stringify(me));
        setUser(me);
      })
      .catch((e) => {
        if ([401, 403].includes(e.response?.status)) logout();
      })
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    if (data.user?.role !== "admin") {
      throw new Error(L("This account does not have administrator permissions.", "Esta conta não tem permissões de administrador."));
    }
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const isAdmin = !!token && user?.role === "admin";

  return (
    <AuthContext.Provider value={{ token, user, isAdmin, checking, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
